import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  OutlinedInput, 
  Chip,
  SelectChangeEvent, 
  Paper, 
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { Idea, Tag, Space } from '../types';
import { api } from '../services/api';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { SubmitContent, contentService } from '../services/content';
import LoginForm from './LoginForm';

const TEST_USER_ID = process.env.REACT_APP_TEST_USER_ID || "1f31fad7-b26f-47b4-8130-94dc2203b379";

// File size limits
const MAX_FILE_SIZE_MB = 15; // Increased to 15MB
const COMPRESSION_THRESHOLD_MB = 5; // Start compressing files larger than 5MB

// Styled component for visually hidden input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface IdeaFormProps {
  onSubmit: (idea: Omit<Idea, 'id' | 'upvotes' | 'commentsCount' | 'created_at' | 'likes'>, imageFile?: File) => Promise<void>;
}

const IdeaForm: React.FC<IdeaFormProps> = ({ onSubmit }) => {
  const { isAuthenticated, currentUser, databaseUserId } = useAuth();
  const { tenantConfig } = useTenant();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<string>('');
  const [selectedSpaceSlug, setSelectedSpaceSlug] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [availableSpaces, setAvailableSpaces] = useState<Space[]>([]);
  const [submitContent, setSubmitContent] = useState<SubmitContent | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Load tenant-specific content
  useEffect(() => {
    const loadContent = async () => {
      try {
        const content = await contentService.getSubmitContent(tenantConfig?.tenant_id);
        setSubmitContent(content);
      } catch (error) {
        console.error('Failed to load submit content:', error);
      }
    };

    loadContent();
  }, [tenantConfig?.tenant_id]);

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        const tags = await api.getTags();
        setAvailableTags(tags);
      } catch (err) {
        console.error('Failed to fetch tags:', err);
        setError('Failed to load available tags. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  // Fetch available spaces for all tenants
  useEffect(() => {
    const fetchSpaces = async () => {
      if (tenantConfig?.tenant_id) {
        try {
          const spaces = await api.getSpaces();
          setAvailableSpaces(spaces);
          console.log(`Fetched ${spaces.length} spaces for tenant: ${tenantConfig.tenant_id}`);
        } catch (err) {
          console.error('Failed to fetch spaces:', err);
          setError('Failed to load available spaces. Please refresh and try again.');
        }
      }
    };

    fetchSpaces();
  }, [tenantConfig?.tenant_id]);

  const handleTagChange = (event: SelectChangeEvent<string>) => {
    setSelectedTagId(event.target.value);
  };

  const handleSpaceChange = (event: SelectChangeEvent<string>) => {
    setSelectedSpaceSlug(event.target.value);
  };

  // Get the selected tag object from its ID
  const getSelectedTag = (): Tag | undefined => {
    return availableTags.find(tag => tag.id === selectedTagId);
  };

  // Get the selected space ID from the slug
  const getSelectedSpaceId = (): string | undefined => {
    const space = availableSpaces.find(space => space.slug === selectedSpaceSlug);
    return space?.id;
  };

  // Compress image if needed
  const compressImageIfNeeded = async (file: File): Promise<File> => {
    const fileSizeMB = file.size / 1024 / 1024;
    
    // If file is smaller than the compression threshold, return it as is
    if (fileSizeMB <= COMPRESSION_THRESHOLD_MB) {
      return file;
    }
    
    try {
      setCompressing(true);
      setCompressionProgress(0);
      
      // Configure compression options
      const options = {
        maxSizeMB: COMPRESSION_THRESHOLD_MB,
        maxWidthOrHeight: 1920, // Limit max width or height to 1920px
        useWebWorker: true,
        onProgress: (progress: number) => {
          setCompressionProgress(progress);
        }
      };
      
      // Compress the image
      const compressedFile = await imageCompression(file, options);
      
      console.log('Original file size:', fileSizeMB.toFixed(2), 'MB');
      console.log('Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
      
      // Create a new file with the same name but compressed data
      return new File([compressedFile], file.name, { 
        type: compressedFile.type,
        lastModified: file.lastModified
      });
    } catch (err) {
      console.error('Error compressing image:', err);
      // If compression fails, return the original file
      return file;
    } finally {
      setCompressing(false);
      setCompressionProgress(100);
    }
  };

  // Handle image file selection
  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Check file size against our max limit (15MB)
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`Image size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please select a smaller image.`);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (JPEG, PNG, etc.).');
        return;
      }
      
      try {
        // Compress image if needed
        const processedFile = await compressImageIfNeeded(file);
        
        // Update state with the processed file
        setImageFile(processedFile);
        
        // Revoke previous object URL if exists
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
        
        // Create new preview
        setImagePreview(URL.createObjectURL(processedFile));
        setError(null);
      } catch (err) {
        console.error('Error processing image:', err);
        setError('Failed to process the image. Please try again with a different image.');
      }
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setImageFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const validateForm = () => {
    // Basic validation
    if (!title.trim()) {
      setError('Title is required');
      return false;
    }
    
    if (!description.trim()) {
      setError('Description is required');
      return false;
    }
    
    if (!selectedTagId) {
      setError('Please select a tag');
      return false;
    }

    // For multi-space tenants, space selection is required
    if (tenantConfig?.tenant_type === 'multi_space' && !selectedSpaceSlug) {
      setError('Please select a space for your idea');
      return false;
    }

    // Ensure we have at least one available space
    if (availableSpaces.length === 0) {
      console.error(`No spaces available for tenant: ${tenantConfig?.tenant_id} (type: ${tenantConfig?.tenant_type})`);
      setError('No spaces available. Please contact support.');
      return false;
    }
    
    console.log(`Validation passed: ${availableSpaces.length} spaces available for tenant: ${tenantConfig?.tenant_id}`);
    console.log('Available spaces:', availableSpaces.map(s => ({ id: s.id, name: s.name })));
    
    return true;
  };

  // Check if this is a multi-space tenant
  const isMultiSpace = tenantConfig?.tenant_type === 'multi_space';
  const spaces = tenantConfig?.spaces || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!isAuthenticated) {
      setShowLoginForm(true);
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Use authenticated user's information
      const userName = currentUser?.displayName || '';
      
      // Ensure we have a valid database UUID
      let userId = databaseUserId;
      
      // If we don't have a database UUID yet, try to get it
      if (!userId && currentUser?.uid) {
        userId = await api.getUserDatabaseUUID(currentUser.uid);
        
        // If we still don't have a valid UUID, show an error
        if (!userId) {
          throw new Error("Could not retrieve valid user ID from the database");
        }
      }
      
      console.log('Using database user_id for idea submission:', userId);
      
      // Determine space_id: for multi-space use selected, for single-space use the only available space
      let spaceId: string | undefined;
      if (isMultiSpace) {
        spaceId = getSelectedSpaceId();
      } else if (availableSpaces.length > 0) {
        // For single-space tenants, use the first (and only) available space
        spaceId = availableSpaces[0].id;
      }
      
      console.log('Using space_id for idea submission:', spaceId);
      
      await onSubmit({
        title,
        description,
        tags: getSelectedTag() ? [getSelectedTag()!] : [],
        user_id: userId || TEST_USER_ID,
        user_display_name: userName,
        comment_count: 0,
        space_id: spaceId,
        is_anonymous: isAnonymous,
      }, imageFile || undefined);
      
      // Reset form on success
      setTitle('');
      setDescription('');
      setSelectedTagId('');
      setSelectedSpaceSlug('');
      setIsAnonymous(false);
      handleRemoveImage();
      setSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting idea:', err);
      setError('Failed to submit your idea. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseLoginForm = () => {
    setShowLoginForm(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {submitContent?.header.title || 'Submit Your Idea'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        {submitContent?.header.subtitle || 'Share your vision for the future of our community space.'}
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ my: 2 }}>
          Your idea was submitted successfully! Thank you for your contribution.
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      
      {showLoginForm ? (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Login to Submit Your Idea
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please login or create an account to submit your idea.
          </Typography>
                          <LoginForm onClose={handleCloseLoginForm} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="text" 
              color="primary" 
              onClick={handleCloseLoginForm}
            >
              Back to Form
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {isAuthenticated && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You will submit this idea as{' '}
              <strong>{currentUser?.displayName || currentUser?.email}</strong>
            </Alert>
          )}
          
          <TextField
            label="Title"
            required
            fullWidth
            margin="normal"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a clear, descriptive title for your idea"
            inputProps={{ maxLength: 100 }}
            helperText={`${title.length}/100 characters`}
          />
          
          <TextField
            label="Description"
            required
            fullWidth
            multiline
            rows={6}
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your idea in detail. What would it involve? How would it benefit the community?"
          />

          {isMultiSpace && availableSpaces.length > 0 && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Select Space *
              </Typography>
              <FormControl fullWidth required>
                <Select
                  value={selectedSpaceSlug}
                  onChange={handleSpaceChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Choose which space this idea is for</em>
                  </MenuItem>
                  {availableSpaces.map((space) => (
                    <MenuItem key={space.slug} value={space.slug}>
                      <Box>
                        <Typography variant="body1">{space.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {space.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Category Tag *
            </Typography>
            <FormControl fullWidth required>
              <Select
                value={selectedTagId}
                onChange={handleTagChange}
                disabled={loading}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select a tag for your idea</em>
                </MenuItem>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  availableTags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
          
          {/* Anonymous Posting Option - Only for Evanston tenant */}
          {tenantConfig?.tenant_id === 'evanston' && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Tooltip 
                title={isAnonymous 
                  ? "Your name will be hidden from other users, but administrators will still be able to see who submitted this idea."
                  : "Click to hide your name from other users"
                }
                arrow
                placement="right"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      disabled={submitting}
                      icon={<VisibilityIcon color="action" />}
                      checkedIcon={<VisibilityOffIcon color="primary" />}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {isAnonymous 
                          ? "Posting anonymously" 
                          : `Posting as ${currentUser?.displayName || currentUser?.email || 'yourself'}`
                        }
                      </Typography>
                      {isAnonymous && (
                        <Typography variant="caption" color="text.secondary">
                          (Admins can still see your identity)
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Tooltip>
            </Box>
          )}
          
          {/* Image Upload Section */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Add an Image (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {submitContent?.imageUpload?.description || 
                `Add a visual representation of your idea. Images help others understand your vision better.
                Supported formats: JPEG, PNG, GIF, etc. (Max size: ${MAX_FILE_SIZE_MB}MB)`}
            </Typography>
            
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2 }}
              disabled={submitting || compressing}
            >
              Upload Image
              <VisuallyHiddenInput 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            
            {compressing && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                  Compressing image... {Math.round(compressionProgress)}%
                </Typography>
                <LinearProgress variant="determinate" value={compressionProgress} />
              </Box>
            )}
            
            {imagePreview && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    maxWidth: '400px',
                    height: '200px',
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                    }} 
                  />
                  <Button 
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleRemoveImage}
                    disabled={submitting}
                    startIcon={<DeleteIcon />}
                    sx={{ 
                      position: 'absolute', 
                      bottom: 8, 
                      right: 8,
                      opacity: 0.9,
                    }}
                  >
                    Remove
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {imageFile?.name} • {(imageFile?.size ? (imageFile.size / 1024 / 1024).toFixed(2) : 0)} MB
                  {imageFile && imageFile.size > COMPRESSION_THRESHOLD_MB * 1024 * 1024 && " (compressed)"}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={submitting || compressing}
              sx={{ minWidth: 120 }}
            >
              {submitting ? <CircularProgress size={24} /> : isAuthenticated ? 'Submit Idea' : 'Login to Submit'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default IdeaForm; 